import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import PinModal from '../components/PinModal';
import DocumentUpload from '../components/DocumentUpload';
import PatientHistoryExport from '../components/PatientHistoryExport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Edit, Trash2, Calendar, Phone, Mail, User, FileText, CreditCard, MapPin, Users, AlertCircle, Pill, Heart, ClipboardList, Building2, Plus, Shield, CheckCircle, XCircle, FileUp, Download } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARISHES = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester",
  "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas"
];

const RELATIONSHIPS = ["Parent", "Spouse", "Child", "Sibling", "Guardian", "Other"];

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccessClinical, canAccessBilling, isOwner } = useAuth();
  const [patient, setPatient] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [encounterSummary, setEncounterSummary] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Packet D: Patient History Export state
  const [showHistoryExport, setShowHistoryExport] = useState(false);
  
  // Phase 6.4: Insurance state
  const [insuranceRecords, setInsuranceRecords] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [insuranceFormData, setInsuranceFormData] = useState({
    insurance_provider_id: '',
    policy_number: '',
    member_name: '',
    coverage_type: '',
    active: true
  });

  useEffect(() => {
    fetchData();
    fetchInsuranceProviders();
  }, [id]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/patients/${id}`),
        axios.get(`${API}/patients/${id}/insurance`)
      ];
      
      // Clinical staff get full encounters
      if (canAccessClinical) {
        requests.push(axios.get(`${API}/encounters?patient_id=${id}`));
      } else {
        // Reception gets summaries only
        requests.push(axios.get(`${API}/encounters-summary?patient_id=${id}`));
      }
      
      // Only owner gets invoices
      if (canAccessBilling) {
        requests.push(axios.get(`${API}/invoices?patient_id=${id}`));
      }
      
      const responses = await Promise.all(requests);
      setPatient(responses[0].data);
      setFormData(responses[0].data);
      setInsuranceRecords(responses[1].data || []);
      
      if (canAccessClinical) {
        setEncounters(responses[2].data);
      } else {
        setEncounterSummary(responses[2].data);
      }
      
      if (canAccessBilling && responses[3]) {
        setInvoices(responses[3].data);
      }
    } catch (error) {
      console.error('Failed to fetch patient:', error);
      toast.error('Failed to load patient');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchInsuranceProviders = async () => {
    try {
      const response = await axios.get(`${API}/insurance-providers?active_only=true`);
      setInsuranceProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch insurance providers:', error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/patients/${id}`, formData);
      setPatient(response.data);
      setShowEditModal(false);
      toast.success('Patient updated');
    } catch (error) {
      toast.error('Failed to update patient');
    } finally {
      setSaving(false);
    }
  };

  // Phase 6.4: Insurance functions
  const resetInsuranceForm = () => {
    setInsuranceFormData({
      insurance_provider_id: '',
      policy_number: '',
      member_name: '',
      coverage_type: '',
      active: true
    });
    setEditingInsurance(null);
  };

  const handleOpenAddInsurance = () => {
    resetInsuranceForm();
    setShowInsuranceModal(true);
  };

  const handleOpenEditInsurance = (record) => {
    setInsuranceFormData({
      insurance_provider_id: record.insurance_provider_id,
      policy_number: record.policy_number,
      member_name: record.member_name || '',
      coverage_type: record.coverage_type || '',
      active: record.active
    });
    setEditingInsurance(record);
    setShowInsuranceModal(true);
  };

  const handleSaveInsurance = async (e) => {
    e.preventDefault();
    if (!insuranceFormData.insurance_provider_id) {
      toast.error('Please select an insurance provider');
      return;
    }
    if (!insuranceFormData.policy_number.trim()) {
      toast.error('Policy number is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingInsurance) {
        await axios.put(`${API}/patients/${id}/insurance/${editingInsurance.id}`, insuranceFormData);
        toast.success('Insurance record updated');
      } else {
        await axios.post(`${API}/patients/${id}/insurance`, insuranceFormData);
        toast.success('Insurance record added');
      }
      setShowInsuranceModal(false);
      resetInsuranceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save insurance record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateInsurance = async (recordId) => {
    if (!window.confirm('Are you sure you want to deactivate this insurance record?')) return;
    
    try {
      await axios.delete(`${API}/patients/${id}/insurance/${recordId}`);
      toast.success('Insurance record deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate insurance record');
    }
  };

  const handleDelete = async (pin) => {
    try {
      await axios.delete(`${API}/patients/${id}?pin=${pin}`);
      toast.success('Patient deleted');
      navigate('/patients');
    } catch (error) {
      throw error;
    }
  };

  const initiateDelete = () => {
    if (isOwner) {
      setShowDeleteConfirm(false);
      setShowPinModal(true);
    } else {
      toast.error('Only Owner Admin can delete patients');
    }
  };

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!patient) return null;

  return (
    <Layout>
      <div className="page-container" data-testid="patient-detail-page">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/patients')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Patients
          </button>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold font-heading">
                {(patient.last_name || patient.first_name || patient.full_name || 'U').charAt(0)}
              </div>
              <div>
                <h1 className="page-title">
                  {patient.last_name && patient.first_name 
                    ? `${patient.last_name}, ${patient.first_name}${patient.middle_name ? ` ${patient.middle_name.charAt(0)}.` : ''}`
                    : patient.full_name
                  }
                </h1>
                <p className="text-slate-500">
                  {calculateAge(patient.date_of_birth)} years old • {patient.gender} {patient.parish && `• ${patient.parish}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Packet D: Export History Button - Owner only */}
              {isOwner && (
                <Button
                  variant="outline"
                  onClick={() => setShowHistoryExport(true)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  data-testid="export-history-btn"
                >
                  <Download className="w-4 h-4" />
                  Export History
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2"
                data-testid="edit-patient-btn"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="delete-patient-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Date of Birth</p>
              <p className="font-medium text-slate-900">{patient.date_of_birth}</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
              <p className="font-medium text-slate-900">{patient.phone || '-'}</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
              <p className="font-medium text-slate-900 truncate">{patient.email || '-'}</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Parish</p>
              <p className="font-medium text-slate-900">{patient.parish || '-'}</p>
            </div>
          </div>
        </div>

        {/* Clinical Summary - Only for clinical staff */}
        {canAccessClinical && (
          <div className="card mb-6" data-testid="clinical-summary">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Clinical Summary
              </h2>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-sm font-semibold text-red-700">Allergies</p>
                </div>
                <p className="text-sm text-red-900 whitespace-pre-wrap">{patient.allergies || 'None documented'}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-4 h-4 text-amber-600" />
                  <p className="text-sm font-semibold text-amber-700">Chronic Illnesses</p>
                </div>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{patient.chronic_illnesses || 'None documented'}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Pill className="w-4 h-4 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-700">Current Medications</p>
                </div>
                <p className="text-sm text-blue-900 whitespace-pre-wrap">{patient.current_medications || 'None documented'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Next of Kin */}
        {(patient.nok_name || patient.nok_telephone) && (
          <div className="card mb-6" data-testid="next-of-kin">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                <Users className="w-5 h-5" />
                Next of Kin
              </h2>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Name</p>
                <p className="font-medium text-slate-900">{patient.nok_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Relationship</p>
                <p className="font-medium text-slate-900">{patient.nok_relationship || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Telephone</p>
                <p className="font-medium text-slate-900">{patient.nok_telephone || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Address</p>
                <p className="font-medium text-slate-900">{patient.nok_address || '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Insurance Records - Phase 6.4 */}
        <div className="card mb-6" data-testid="patient-insurance">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Insurance Coverage ({insuranceRecords.filter(r => r.active).length} active)
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenAddInsurance}
              className="border-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Insurance
            </Button>
          </div>
          {insuranceRecords.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {insuranceRecords.map((record) => (
                <div
                  key={record.id}
                  className={`p-4 ${!record.active ? 'bg-slate-50 opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${record.active ? 'bg-blue-100' : 'bg-slate-200'}`}>
                        <Shield className={`w-5 h-5 ${record.active ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{record.insurance_provider_name}</p>
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-600">
                            {record.insurance_provider_code}
                          </span>
                          {record.active ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Policy: <span className="font-mono">{record.policy_number}</span>
                        </p>
                        {record.member_name && (
                          <p className="text-sm text-slate-500">Member: {record.member_name}</p>
                        )}
                        {record.coverage_type && (
                          <p className="text-sm text-slate-500">Coverage: {record.coverage_type}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEditInsurance(record)}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {record.active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivateInsurance(record.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No insurance records</p>
              <p className="text-sm text-slate-500">Add insurance coverage for this patient</p>
            </div>
          )}
        </div>

        {/* Documents Section (Packet 3) */}
        <div className="card p-4">
          <DocumentUpload patientId={id} />
        </div>

        {/* Encounters & Invoices - Role-based display */}
        <div className={`grid grid-cols-1 ${canAccessBilling ? 'lg:grid-cols-2' : ''} gap-6`}>
          {/* Encounters - Clinical staff see full details, Reception sees summary */}
          {canAccessClinical ? (
            <div className="card" data-testid="patient-encounters">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Encounters ({encounters.length})
                </h2>
                <Button
                  size="sm"
                  onClick={() => navigate(`/encounters?patient_id=${id}&action=new`)}
                  className="btn-primary"
                  data-testid="add-encounter-btn"
                >
                  Add Encounter
                </Button>
              </div>
              {encounters.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {encounters.map((enc) => (
                    <div
                      key={enc.id}
                      className="p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/encounters/${enc.id}`)}
                      data-testid={`encounter-item-${enc.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">
                            {enc.presenting_complaint?.substring(0, 40) || enc.assessment?.substring(0, 40) || 'No notes'}
                            {(enc.presenting_complaint?.length > 40 || enc.assessment?.length > 40) ? '...' : ''}
                          </p>
                          {enc.signed_at && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Signed</span>
                          )}
                        </div>
                        <span className="text-sm text-slate-500">{enc.date}</span>
                      </div>
                      {enc.assessment && (
                        <p className="text-sm text-slate-500 mt-1 truncate">Assessment: {enc.assessment}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">No encounters yet</div>
              )}
            </div>
          ) : (
            /* Reception sees appointment summary only */
            <div className="card" data-testid="patient-appointments">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Appointments ({encounterSummary.length})
                </h2>
              </div>
              {encounterSummary.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {encounterSummary.map((enc) => (
                    <div
                      key={enc.id}
                      className="p-4 flex items-center justify-between"
                      data-testid={`appointment-item-${enc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{new Date(enc.date).toLocaleDateString()}</p>
                          <p className="text-sm text-slate-500">Appointment</p>
                        </div>
                      </div>
                      {enc.signed_at && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Completed</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">No appointments yet</div>
              )}
            </div>
          )}

          {/* Invoices - Owner only */}
          {canAccessBilling && (
            <div className="card" data-testid="patient-invoices">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Invoices ({invoices.length})
                </h2>
                <Button
                  size="sm"
                  onClick={() => navigate(`/billing?patient_id=${id}&action=new`)}
                  className="btn-primary"
                  data-testid="add-invoice-btn"
                >
                  Add Invoice
                </Button>
              </div>
              {invoices.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {invoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/billing/${inv.id}`)}
                      data-testid={`invoice-item-${inv.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="invoice-id">{inv.invoice_number}</span>
                          <p className="font-medium text-slate-900 mt-1">{inv.description || inv.notes || 'Invoice'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">${(inv.grand_total || inv.amount || 0).toFixed(2)}</p>
                          <span className={['Paid', 'paid'].includes(inv.status) ? 'badge-paid' : 'badge-unpaid'}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">No invoices yet</div>
              )}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-patient-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Patient</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-6">
              {/* Personal Details */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Personal Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit_first_name">First Name</Label>
                    <Input
                      id="edit_first_name"
                      value={formData.first_name || ''}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      data-testid="edit-first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_middle_name">Middle Name</Label>
                    <Input
                      id="edit_middle_name"
                      value={formData.middle_name || ''}
                      onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                      data-testid="edit-middle-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_last_name">Last Name</Label>
                    <Input
                      id="edit_last_name"
                      value={formData.last_name || ''}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      data-testid="edit-last-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="edit_dob">Date of Birth</Label>
                    <Input
                      id="edit_dob"
                      type="date"
                      value={formData.date_of_birth || ''}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      data-testid="edit-dob-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_gender">Gender</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger data-testid="edit-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit_phone">Phone</Label>
                    <Input
                      id="edit_phone"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="edit-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_email">Email</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="edit-email-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit_address">Address</Label>
                    <Input
                      id="edit_address"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      data-testid="edit-address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_parish">Parish</Label>
                    <Select
                      value={formData.parish || ''}
                      onValueChange={(value) => setFormData({ ...formData, parish: value })}
                    >
                      <SelectTrigger data-testid="edit-parish-select">
                        <SelectValue placeholder="Select parish" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARISHES.map((parish) => (
                          <SelectItem key={parish} value={parish}>{parish}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Clinical Summary - Only for clinical staff */}
              {canAccessClinical && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Summary</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit_chronic_illnesses">Chronic Illnesses</Label>
                      <Textarea
                        id="edit_chronic_illnesses"
                        value={formData.chronic_illnesses || ''}
                        onChange={(e) => setFormData({ ...formData, chronic_illnesses: e.target.value })}
                        rows={2}
                        data-testid="edit-chronic-illnesses-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_medications">Current Medications</Label>
                      <Textarea
                        id="edit_medications"
                        value={formData.current_medications || ''}
                        onChange={(e) => setFormData({ ...formData, current_medications: e.target.value })}
                        rows={2}
                        data-testid="edit-medications-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_allergies">Allergies</Label>
                      <Textarea
                        id="edit_allergies"
                        value={formData.allergies || ''}
                        onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                        rows={2}
                        data-testid="edit-allergies-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Next of Kin */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Next of Kin</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_nok_name">Name</Label>
                    <Input
                      id="edit_nok_name"
                      value={formData.nok_name || ''}
                      onChange={(e) => setFormData({ ...formData, nok_name: e.target.value })}
                      data-testid="edit-nok-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_nok_relationship">Relationship</Label>
                    <Select
                      value={formData.nok_relationship || ''}
                      onValueChange={(value) => setFormData({ ...formData, nok_relationship: value })}
                    >
                      <SelectTrigger data-testid="edit-nok-relationship-select">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIPS.map((rel) => (
                          <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit_nok_telephone">Telephone</Label>
                    <Input
                      id="edit_nok_telephone"
                      value={formData.nok_telephone || ''}
                      onChange={(e) => setFormData({ ...formData, nok_telephone: e.target.value })}
                      data-testid="edit-nok-telephone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_nok_address">Address</Label>
                    <Input
                      id="edit_nok_address"
                      value={formData.nok_address || ''}
                      onChange={(e) => setFormData({ ...formData, nok_address: e.target.value })}
                      data-testid="edit-nok-address-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="update-patient-btn">
                  {saving ? 'Saving...' : 'Update'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md border-2 border-red-200" data-testid="delete-confirm-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-900">Delete Patient</DialogTitle>
            </DialogHeader>
            <p className="text-slate-700">
              Are you sure you want to delete {patient.last_name && patient.first_name 
                ? `${patient.last_name}, ${patient.first_name}` 
                : patient.full_name}? This action cannot be undone and requires PIN verification.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDeleteConfirm(false)} className="border-2">
                Cancel
              </Button>
              <Button
                onClick={initiateDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Continue to PIN
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PIN Modal for Delete */}
        <PinModal
          isOpen={showPinModal}
          onClose={() => setShowPinModal(false)}
          onVerify={handleDelete}
          title="Delete Patient"
          description={`Enter your PIN to permanently delete ${patient?.last_name && patient?.first_name 
            ? `${patient.last_name}, ${patient.first_name}` 
            : patient?.full_name}`}
        />

        {/* Insurance Modal - Phase 6.4 */}
        <Dialog open={showInsuranceModal} onOpenChange={setShowInsuranceModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                {editingInsurance ? 'Edit Insurance Record' : 'Add Insurance Record'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveInsurance} className="space-y-4 pt-4">
              <div>
                <Label>Insurance Provider *</Label>
                <Select
                  value={insuranceFormData.insurance_provider_id}
                  onValueChange={(value) => setInsuranceFormData({ ...insuranceFormData, insurance_provider_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select insurance provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {insuranceProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name} ({provider.short_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="policy_number">Policy Number *</Label>
                <Input
                  id="policy_number"
                  value={insuranceFormData.policy_number}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, policy_number: e.target.value })}
                  placeholder="e.g., SAG-12345-67890"
                  required
                />
              </div>
              <div>
                <Label htmlFor="member_name">Member Name (if different from patient)</Label>
                <Input
                  id="member_name"
                  value={insuranceFormData.member_name}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, member_name: e.target.value })}
                  placeholder="Name on insurance card"
                />
              </div>
              <div>
                <Label htmlFor="coverage_type">Coverage Type</Label>
                <Input
                  id="coverage_type"
                  value={insuranceFormData.coverage_type}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, coverage_type: e.target.value })}
                  placeholder="e.g., Full coverage, Basic plan"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-xs text-slate-500">Only active insurance can be used for billing</p>
                </div>
                <Switch
                  checked={insuranceFormData.active}
                  onCheckedChange={(checked) => setInsuranceFormData({ ...insuranceFormData, active: checked })}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInsuranceModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingInsurance ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Packet D: Patient History Export Modal */}
        {isOwner && (
          <PatientHistoryExport
            patient={patient}
            isOpen={showHistoryExport}
            onClose={() => setShowHistoryExport(false)}
          />
        )}
      </div>
    </Layout>
  );
};

export default PatientDetail;
